// src/components/shared/Paginacion.jsx
import React, { useMemo } from "react";

export default function Paginacion({
  pagina,
  totalPaginas,
  onAnterior,
  onSiguiente,
  onGoToPage,             // opcional: (n) => void
  totalItems,             // opcional: muestra "X–Y de N"
  porPagina,              // opcional: junto a totalItems
  showSummary = true,     // muestra el resumen compacto
  showFirstLast = false,  // añade ⏮/⏭
  maxButtons = 5,         // nº de botones numéricos visibles (desktop)
}) {
  const canPrev = pagina > 1;
  const canNext = pagina < totalPaginas;

  const { start, end } = useMemo(() => {
    if (!totalItems || !porPagina) return { start: null, end: null };
    const s = (pagina - 1) * porPagina + 1;
    const e = Math.min(pagina * porPagina, totalItems);
    return { start: s, end: e };
  }, [pagina, porPagina, totalItems]);

  // Ventana de páginas con elipsis (solo usada en desktop)
  const pages = useMemo(() => {
    const p = Math.max(1, Math.min(pagina, totalPaginas));
    const total = Math.max(1, totalPaginas);
    const max = Math.max(3, maxButtons);

    if (total <= max) return Array.from({ length: total }, (_, i) => i + 1);

    const left = Math.max(2, p - Math.floor((max - 2) / 2));
    const right = Math.min(total - 1, left + (max - 3));
    const adjustedLeft = Math.max(2, right - (max - 3));

    const arr = [1];
    if (adjustedLeft > 2) arr.push("ellipsis-left");
    for (let i = adjustedLeft; i <= right; i++) arr.push(i);
    if (right < total - 1) arr.push("ellipsis-right");
    arr.push(total);
    return arr;
  }, [pagina, totalPaginas, maxButtons]);

  const renderBtn = (key, label, { onClick, disabled = false, current = false, title }) => (
    <button
      key={key}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      aria-current={current ? "page" : undefined}
      title={title}
      className={[
        "h-10 min-w-[40px] px-3 rounded-lg border text-sm font-medium transition tabular-nums",
        "inline-flex items-center justify-center select-none",
        disabled
          ? "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed"
          : current
          ? "bg-slate-900 text-white border-slate-900 shadow-sm"
          : "bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50",
        "focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300"
      ].join(" ")}
    >
      {label}
    </button>
  );

  return (
    <div
      className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 shadow-sm backdrop-blur"
      role="navigation"
      aria-label="Paginación"
    >
      {/* Resumen compacto */}
      {showSummary ? (
        <div className="text-sm text-slate-600" aria-live="polite">
          {start && end ? (
            <>
              Mostrando{" "}
              <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-0.5 font-semibold text-slate-800">
                {start}–{end}
              </span>{" "}
              de <span className="font-semibold text-slate-800">{totalItems}</span>
            </>
          ) : (
            <>
              Página <span className="font-semibold text-slate-800">{pagina}</span> de{" "}
              <span className="font-semibold text-slate-800">{totalPaginas}</span>
            </>
          )}
        </div>
      ) : (
        <span />
      )}

      {/* Controles */}
      <div className="flex items-center gap-2 w-full sm:w-auto sm:justify-end rounded-xl border border-slate-200 bg-slate-50/70 px-2 py-1">
        {showFirstLast &&
          renderBtn(
            "first",
            "⏮ Primera",
            {
              onClick: () => onGoToPage && onGoToPage(1),
              disabled: !onGoToPage || !canPrev,
              title: "Primera página",
            }
          )}

        {renderBtn("prev", "← Anterior", { onClick: onAnterior, disabled: !canPrev, title: "Página anterior" })}

        {/* --- Móvil: selector compacto --- */}
        <div className="flex sm:hidden items-center gap-2">
          <label htmlFor="page-select" className="text-xs text-gray-600">
            Página
          </label>
          <select
            id="page-select"
            value={pagina}
            onChange={(e) => onGoToPage && onGoToPage(Number(e.target.value))}
            disabled={!onGoToPage}
            className={[
              "px-3 py-2 rounded-xl border text-sm",
              "min-h-[40px] bg-white text-gray-700 border-gray-300",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
            ].join(" ")}
            aria-label="Seleccionar página"
          >
            {Array.from({ length: totalPaginas || 1 }, (_, i) => i + 1).map((p) => (
              <option key={p} value={p}> {p} / {totalPaginas} </option>
            ))}
          </select>
        </div>

        {/* --- Desktop: botones numéricos con elipsis --- */}
        <div className="hidden sm:flex items-center gap-1">
          {pages.map((n, idx) =>
            typeof n === "number"
              ? renderBtn(
                  `p-${n}`,
                  n,
                  {
                    onClick: () => onGoToPage && onGoToPage(n),
                    disabled: !onGoToPage,
                    current: n === pagina,
                    title: `Ir a la página ${n}`,
                  }
                )
              : (
                <span
                  key={`${n}-${idx}`}
                  className="px-2 text-gray-400 select-none"
                  aria-hidden="true"
                >
                  …
                </span>
              )
          )}
        </div>

        {renderBtn("next", "Siguiente →", { onClick: onSiguiente, disabled: !canNext, title: "Página siguiente" })}

        {showFirstLast &&
          renderBtn(
            "last",
            "Última ⏭",
            {
              onClick: () => onGoToPage && onGoToPage(totalPaginas),
              disabled: !onGoToPage || !canNext,
              title: "Última página",
            }
          )}
      </div>
    </div>
  );
}
